import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react-native';
import { apiService } from '../services/apiService';
import logger from '../utils/logger';

// 10-second pre-fetch warm window: a profile-screen mount or settings-link
// hover calls `prefetch()`; the modal then consumes the cached response.
// <=5s old → reuse as-is. 5–10s → silent refresh. >10s → discard.
const PREFETCH_FRESH_MS = 5_000;
const PREFETCH_SOFT_MS = 10_000;

let _cache = null; // { data, fetchedAt, userId }
const listeners = new Set();

function notify() {
  for (const l of listeners) l();
}

async function doFetch({ force = false, userId } = {}) {
  const now = Date.now();
  if (!force && _cache && _cache.userId === userId && now - _cache.fetchedAt < PREFETCH_FRESH_MS) {
    return _cache.data;
  }

  const body = await apiService.invokeEdgeFunction('get-billing-summary', {
    method: 'POST',
    body: {},
  });

  _cache = { data: body, fetchedAt: Date.now(), userId };
  notify();
  return body;
}

export function useBillingSummary({ userId, enabled = true } = {}) {
  const [data, setData] = useState(() => (_cache && _cache.userId === userId ? _cache.data : null));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async ({ force = false } = {}) => {
    if (!userId) return null;
    setIsLoading(true);
    setError(null);
    try {
      const fresh = await doFetch({ force, userId });
      setData(fresh);
      return fresh;
    } catch (e) {
      setError(e);
      logger.warn('[useBillingSummary] fetch failed', e);
      Sentry.captureException(e, {
        tags: {
          hook: 'useBillingSummary',
          function: 'get-billing-summary',
          status: String(e?.status ?? 'n/a'),
          error_code: e?.code ?? 'n/a',
        },
        contexts: { billing: { user_id: userId, force } },
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!enabled || !userId) return;
    if (_cache && _cache.userId === userId && Date.now() - _cache.fetchedAt < PREFETCH_FRESH_MS) {
      // Fast path: recent prefetch — no network call on modal open.
      setData(_cache.data);
      return;
    }
    if (_cache && _cache.userId === userId && Date.now() - _cache.fetchedAt < PREFETCH_SOFT_MS) {
      // Stale-ish: show cached, silent refresh.
      setData(_cache.data);
      refresh({ force: false });
      return;
    }
    refresh({ force: true });
  }, [enabled, userId, refresh]);

  // Listen for cache invalidations from prefetch() or other hook instances.
  useEffect(() => {
    const listener = () => {
      if (_cache?.userId === userId) setData(_cache.data);
    };
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, [userId]);

  return { data, isLoading, error, refresh };
}

// Exposed imperative prefetch. Call from a link hover or modal-adjacent
// lifecycle to warm the cache.
export function prefetchBillingSummary(userId) {
  if (!userId) return Promise.resolve(null);
  if (_cache && _cache.userId === userId && Date.now() - _cache.fetchedAt < PREFETCH_SOFT_MS) {
    return Promise.resolve(_cache.data);
  }
  return doFetch({ force: true, userId }).catch((e) => {
    logger.warn('[prefetchBillingSummary] suppressed', e);
    Sentry.captureException(e, {
      tags: {
        hook: 'prefetchBillingSummary',
        function: 'get-billing-summary',
        status: String(e?.status ?? 'n/a'),
        error_code: e?.code ?? 'n/a',
      },
      contexts: { billing: { user_id: userId, prefetch: true } },
    });
    return null;
  });
}

export function _resetBillingSummaryCacheForTest() {
  _cache = null;
  listeners.clear();
}
